import React, { useState } from 'react';
import { ExternalLinkIcon, PlusIcon, SearchIcon, Trash2Icon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { analyzeSearchTarget, saveSearchTargets, type SearchTarget } from '../../data/searchTargets';
import type { WebsiteContent } from '../../data/website';
interface SearchTargetsProps {
  content: WebsiteContent;
  targets: SearchTarget[];
  onChange: (targets: SearchTarget[]) => void;
}
const difficultyStyles = {
  strong: 'border-ok/30 bg-ok/10 text-ok',
  realistic: 'border-accent/30 bg-accent/10 text-accent-soft',
  hard: 'border-warn/30 bg-warn/10 text-warn'
} as const;
export function SearchTargets({
  content,
  targets,
  onChange
}: SearchTargetsProps) {
  const [term, setTerm] = useState('');
  const commit = (next: SearchTarget[]) => {
    onChange(next);
    saveSearchTargets(next);
  };
  const add = () => {
    const value = term.trim();
    if (!value) return;
    commit([...targets, {
      id: `target-${Date.now()}`,
      term: value
    }]);
    setTerm('');
  };
  return <Card className="p-4" data-id="element-509" data-mp="3er2aj9c3tm1n">
      <div className="flex gap-2" data-id="element-510" data-mp="3iqt4flwxgiae">
        <label className="sr-only" htmlFor="search-target-input" data-id="element-511" data-mp="3iprvbmzh25vb">
          Search term
        </label>
        <input id="search-target-input" value={term} onChange={event => setTerm(event.target.value)} onKeyDown={event => {
        if (event.key === 'Enter') {
          event.preventDefault();
          add();
        }
      }} placeholder="What would someone type into Google?" className="min-w-0 flex-1 rounded-xl border border-white/[0.1] bg-ink-800 px-3 py-2.5 text-[13px] text-white outline-none placeholder:text-zinc-600 focus:border-[#d97742]/70" data-id="element-512" data-mp="34yd7n2umdghx" />
        <Button variant="secondary" size="sm" type="button" onClick={add} data-id="element-513" data-mp="k84dlgvnhkew">
          <PlusIcon className="h-3.5 w-3.5" data-id="element-514" data-mp="k6tc5npogoqy" /> Add
        </Button>
      </div>

      <div className="mt-4 space-y-2.5" data-id="element-515" data-mp="p6z474zv0d9k">
        {targets.map(target => {
        const analysis = analyzeSearchTarget(target, content);
        return <article key={target.id} className="rounded-xl border border-white/[0.08] bg-ink-800 p-3.5" data-id="element-516" data-mp="iqfg8k2c2xho">
              <div className="flex items-start justify-between gap-3" data-id="element-517" data-mp="dtgr6fxj1gqc">
                <div className="min-w-0" data-id="element-518" data-mp="8tuafwihpics">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-519" data-mp="8uvnu7mzagxo">
                    <h3 className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-white" data-id="element-520" data-mp="16hcljdzmiwjy">
                      <SearchIcon className="h-3.5 w-3.5 text-zinc-600" data-id="element-521" data-mp="11j19qxprqbz3" /> {target.term}
                    </h3>
                    <span className={`rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${difficultyStyles[analysis.difficulty]}`} data-id="element-522" data-mp="wpox7lyd5vmp">
                      {analysis.difficultyNote}
                    </span>
                  </div>
                  <p className="mt-2 text-[12.5px] leading-relaxed text-zinc-500" data-id="element-523" data-mp="1kfsyvnrzrlb2">{analysis.advice}</p>
                </div>
                <button type="button" aria-label={`Remove ${target.term}`} onClick={() => commit(targets.filter(entry => entry.id !== target.id))} className="shrink-0 rounded-lg p-1.5 text-zinc-600 transition-colors hover:bg-white/[0.06] hover:text-rose-300" data-id="element-524" data-mp="1rtjw5a797hkh">
                  <Trash2Icon className="h-3.5 w-3.5" data-id="element-525" data-mp="2are9g0ff0f8o" />
                </button>
              </div>

              <div className="mt-3 flex items-center gap-3" data-id="element-526" data-mp="2fpfrci13u8ik">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/[0.07]" data-id="element-527" data-mp="2fnuwssesipkp">
                  <div className={`h-full rounded-full ${analysis.coverage >= 100 ? 'bg-ok' : analysis.coverage >= 60 ? 'bg-accent' : 'bg-warn'}`} style={{
                width: `${Math.max(3, analysis.coverage)}%`
              }} data-id="element-528" data-mp="2kir2gvcy9bga" />
                </div>
                <span className="shrink-0 text-[11px] text-zinc-500" data-id="element-529" data-mp="2ygysejbkjchy">{analysis.coverage}% of these words are on your site</span>
              </div>

              <dl className="mt-3 space-y-1.5 text-[12px]" data-id="element-530" data-mp="3r6h0csa6olqx">
                <div className="flex gap-2" data-id="element-531" data-mp="3r5yz1h8nzhc5">
                  <dt className="shrink-0 text-zinc-600" data-id="element-532" data-mp="8src7vm4ixgi">Supported by</dt>
                  <dd className="text-zinc-400" data-id="element-533" data-mp="8u2ic231khas">
                    {analysis.found.length ? analysis.found.join(' · ') : 'No section carries this phrase yet.'}
                  </dd>
                </div>
                {analysis.missing.length > 0 && <div className="flex gap-2" data-id="element-534" data-mp="388o7yjx6ru7s">
                    <dt className="shrink-0 text-zinc-600" data-id="element-535" data-mp="3m74h0hiitwt3">Missing words</dt>
                    <dd className="text-warn" data-id="element-536" data-mp="3m51hn6vharbp">
                      {analysis.missing.join(', ')} — add these to your About story, page title, or photo captions.
                    </dd>
                  </div>}
              </dl>
            </article>;
      })}

        {!targets.length && <p className="text-[12.5px] text-zinc-500" data-id="element-537" data-mp="14yues0j91puk">
            No searches listed yet. Start with your band name, each member’s name, and one local phrase like “country
            band Fort Worth”.
          </p>}
      </div>

      <div className="mt-4 rounded-xl border border-white/[0.07] bg-ink-800 p-3" data-id="element-538" data-mp="v1om4nuzlqs8">
        <p className="text-[12px] leading-relaxed text-zinc-500" data-id="element-539" data-mp="zz83vxqw8lox">
          This shows whether your site contains the words people would type. It cannot show your actual position in
          Google — that requires Search Console, which reports the real searches bringing people to you.
        </p>
        <a href="https://search.google.com/search-console" target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-accent-soft transition-colors hover:text-white" data-id="element-540" data-mp="1e00uw8hzkyna">
          Open Search Console <ExternalLinkIcon className="h-3 w-3" data-id="element-541" data-mp="2bemzoy7d97ig" />
        </a>
      </div>
    </Card>;
}